import React, { useEffect, useState } from 'react';
import apiClient from '../services/apiClient';
import { getLanguage, t } from '../i18n';
import ScenePlayer from './ScenePlayer';
import './TournamentEntryScene.css';

const DAY_MS = 24 * 60 * 60 * 1000;

const TOURNAMENT_ENTRY_SCENE_IDS = {
  signup: 'tournament_entry_signup',
  active: 'tournament_entry_active',
  lose: 'tournament_entry_lose',
  cheerup: 'tournament_entry_cheerup',
  completed: 'tournament_entry_completed',
  scheduled: 'tournament_entry_scheduled',
};

function getHumanCount(tournament) {
  return (tournament?.participants || []).filter((participant) => participant.type === 'human').length;
}

function formatDate(value) {
  if (!value) return t('practiceUi.tournament.soon');
  return new Date(value).toLocaleDateString(getLanguage() === 'ko' ? 'ko-KR' : 'en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'Asia/Seoul',
  });
}

function getSummaryNow(summary) {
  return summary?.serverTime ? new Date(summary.serverTime) : new Date();
}

function getCompletedEntryTournament(summary) {
  const now = getSummaryNow(summary);
  const candidates = [
    summary?.lastTournament,
    summary?.currentTournament,
  ];

  return candidates.find((tournament) => (
    tournament?.status === 'completed' &&
    isWithinCompletedEntryWindow(tournament, now)
  )) || null;
}

function getEntryTournament(summary) {
  return getCompletedEntryTournament(summary) || summary?.currentTournament || null;
}

function getUserEntryForTournament(summary, tournament) {
  const currentEntry = summary?.userEntry;
  if (currentEntry && tournament?.participants?.some((participant) => participant.id === currentEntry.id)) {
    return currentEntry;
  }

  if (!currentEntry?.userId) return null;
  return (tournament?.participants || []).find((participant) => (
    participant.type === 'human' &&
    participant.userId === currentEntry.userId
  )) || null;
}

function isUserEliminated(summary, tournament = getEntryTournament(summary), entry = getUserEntryForTournament(summary, tournament)) {

  if (!tournament || !entry) return false;

  const matches = tournament.matches || [];
  const userKnockoutLoss = matches.some((match) => (
    match.status === 'resolved' &&
    match.loserId === entry.id &&
    match.phase !== 'group'
  ));
  const knockoutStarted = matches.some((match) => match.phase === 'roundOf16');
  const userReachedKnockout = matches.some((match) => (
    match.phase !== 'group' &&
    (match.homeId === entry.id || match.awayId === entry.id)
  ));
  const completedResult = (tournament.finalResults || []).find((result) => (
    result.participantId === entry.id || result.userId === entry.userId
  ));

  if (completedResult?.result === 'champion') return false;
  return (
    userKnockoutLoss ||
    (knockoutStarted && !userReachedKnockout) ||
    (tournament.status === 'completed' && completedResult)
  );
}

function getTournamentState(summary) {
  const tournament = getEntryTournament(summary);
  const entry = getUserEntryForTournament(summary, tournament);

  if (!tournament || !entry) return 'not-attending';
  if (isUserEliminated(summary, tournament, entry)) return 'eliminated';

  return 'alive';
}

function getTournamentEndTime(tournament) {
  const explicitEnd = tournament?.endAt || tournament?.endsAt || tournament?.completedAt || tournament?.endedAt;
  const explicitEndMs = explicitEnd ? new Date(explicitEnd).getTime() : NaN;
  if (Number.isFinite(explicitEndMs)) return explicitEndMs;

  const startMs = tournament?.startAt ? new Date(tournament.startAt).getTime() : NaN;
  return Number.isFinite(startMs) ? startMs + 7 * DAY_MS : NaN;
}

function isWithinCompletedEntryWindow(tournament, now) {
  const endMs = getTournamentEndTime(tournament);
  if (!Number.isFinite(endMs)) return tournament?.status === 'completed';

  const nowMs = now instanceof Date ? now.getTime() : new Date(now).getTime();
  return Number.isFinite(nowMs) && nowMs >= endMs && nowMs <= endMs + 7 * DAY_MS;
}

export function getTournamentEntrySceneId(summary) {
  const completedTournament = getCompletedEntryTournament(summary);
  if (completedTournament) return TOURNAMENT_ENTRY_SCENE_IDS.completed;

  const tournament = summary?.currentTournament;
  const entry = summary?.userEntry;

  if (!tournament) return TOURNAMENT_ENTRY_SCENE_IDS.scheduled;
  if (tournament.status === 'signup') return TOURNAMENT_ENTRY_SCENE_IDS.signup;
  if (tournament.status === 'active') {
    if (!entry) return TOURNAMENT_ENTRY_SCENE_IDS.cheerup;
    return isUserEliminated(summary)
      ? TOURNAMENT_ENTRY_SCENE_IDS.lose
      : TOURNAMENT_ENTRY_SCENE_IDS.active;
  }
  return TOURNAMENT_ENTRY_SCENE_IDS.scheduled;
}

function getEventLine(tournament) {
  if (!tournament) return t('practiceUi.tournament.loading');
  if (tournament.status === 'scheduled') {
    return t('practiceUi.tournament.scheduled', undefined, { month: tournament.month, date: formatDate(tournament.signupOpensAt) });
  }
  if (tournament.status === 'signup') {
    return t('practiceUi.tournament.signup', undefined, { month: tournament.month, count: getHumanCount(tournament) });
  }
  if (tournament.status === 'active') {
    return t('practiceUi.tournament.active', undefined, {
      phase: ['groupStage', 'roundOf16', 'quarterFinal', 'semiFinal', 'final'].includes(tournament.currentPhase)
        ? t('dashboard.tournament.labels.' + tournament.currentPhase)
        : t('learningNavigation.tournament.competition'),
      day: Math.min(tournament.currentDay || 1, 7),
    });
  }
  if (tournament.status === 'completed') {
    return t('practiceUi.tournament.completed', undefined, { month: tournament.month });
  }
  if (tournament.status === 'cancelled') {
    return t('practiceUi.tournament.cancelled');
  }
  return t('practiceUi.tournament.current', undefined, { month: tournament.month });
}

function getSupportLine(state, tournament) {
  if (state === 'not-attending') return t('practiceUi.tournament.notAttending');
  if (state === 'eliminated') return t('practiceUi.tournament.eliminated');
  if (tournament?.status === 'signup') return t('practiceUi.tournament.entered');
  if (tournament?.status === 'completed') return t('practiceUi.tournament.final');
  return t('practiceUi.tournament.alive');
}

function TournamentEntryScene({ onContinue }) {
  const [state, setState] = useState({
    status: 'loading',
    summary: null,
    scene: null,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;

    apiClient.getTournamentSummary()
      .then(async (summary) => {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const sceneId = getTournamentEntrySceneId(summary);
        const sceneResult = typeof apiClient.getSceneById === 'function'
          ? await apiClient.getSceneById(sceneId, { timezone }).catch(() => ({ scene: null }))
          : { scene: null };

        if (!cancelled) {
          setState({ status: 'ready', summary, scene: sceneResult?.scene || null, error: null });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ status: 'error', summary: null, scene: null, error });
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const tournament = getEntryTournament(state.summary);
  const tournamentState = state.status === 'ready' ? getTournamentState(state.summary) : null;
  const eventLine = state.status === 'error'
    ? t('practiceUi.tournament.error')
    : getEventLine(tournament);
  const supportLine = state.status === 'error'
    ? t('practiceUi.tournament.retry')
    : getSupportLine(tournamentState, tournament);

  if (state.scene && state.status === 'ready') {
    return (
      <ScenePlayer
        scene={state.scene}
        onContinue={onContinue}
        variant="opponent"
        continueLabel={t('practiceUi.tournament.enter')}
        nextLabel={t('common.next')}
      />
    );
  }

  return (
    <div className="tournament-entry-scene">
      <div className="tournament-entry-panel">
        <div className="tournament-entry-copy">
          <p className="tournament-entry-event">{eventLine}</p>
          <p className="tournament-entry-support">{supportLine}</p>
        </div>
        <button
          type="button"
          className="tournament-entry-button"
          onClick={onContinue}
          disabled={state.status === 'loading'}
        >
          {state.status === 'loading' ? t('practiceUi.tournament.checking') : t('practiceUi.tournament.enter')}
        </button>
      </div>
    </div>
  );
}

export default TournamentEntryScene;
